import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Video, Image, X, Loader2, Shield, Camera, Play, Pause } from 'lucide-react';
import { motion } from 'framer-motion';
import { AIDisclosureForm, isDisclosureComplete } from '../AIDisclosure';
import { GENRES, SuggestAIButton, SuggestedTags, VIDEO_STYLES } from './studioShared';

// Studio music-video upload form (extracted from StudioPage).
export const StudioMusicUploadForm = ({
  artistName,
  axiosInstance,
  canvasRef,
  captureFrame,
  capturedThumbnail,
  clearVideo,
  contentCertified,
  currentTime,
  customThumbnailFile,
  customThumbnailInputRef,
  description,
  disclosure,
  formatTime,
  genre,
  handleCustomThumbnailPick,
  handleSeek,
  handleTimeUpdate,
  handleUpload,
  handleVideoLoaded,
  handleVideoSelect,
  isPlaying,
  musicSuggestedTags,
  processingElapsed,
  processingStage,
  progress,
  rightsConfirmed,
  setCapturedThumbnail,
  setContentCertified,
  setDescription,
  setDisclosure,
  setGenre,
  setIsPlaying,
  setMusicSuggestedTags,
  setRightsConfirmed,
  setTitle,
  setToolsUsed,
  setVideoStyle,
  tab,
  thumbnailTimestamp,
  title,
  togglePlay,
  toolsCatalog,
  toolsUsed,
  uploading,
  useTimestampOnly,
  useTimestampOnlySelected,
  videoDuration,
  videoInputRef,
  videoPreview,
  videoPreviewRef,
  videoStyle,
  videos,
}) => (
  <>
  <div className="flex items-start gap-3 mb-5 px-4 py-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
    <Shield className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
    <div>
      <p className="text-sm text-orange-200 font-medium">AI Content Moderation Active</p>
      <p className="text-xs text-orange-300/70 mt-0.5">All uploads are automatically scanned for prohibited content including violence, nudity, hate speech, and other violations. Content that violates our guidelines will be rejected.</p>
    </div>
  </div>

  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
    {/* Video Upload with Scrubber */}
    <div>
      <Label className="text-zinc-300 mb-2 block text-sm">Video *</Label>
      {videoPreview ? (
        <div className="space-y-3">
          <div className="relative aspect-video bg-zinc-900 rounded-lg overflow-hidden">
            <video 
              ref={videoPreviewRef}
              src={videoPreview} 
              className="w-full h-full object-contain"
              onLoadedMetadata={handleVideoLoaded}
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => setIsPlaying(false)}
              playsInline
              webkit-playsinline="true"
              muted
              preload="auto"
              controls={false}
              data-testid="upload-video-preview"
            />
            <button onClick={clearVideo} className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full hover:bg-black/80 z-10">
              <X className="w-3.5 h-3.5 text-white" />
            </button>
          </div>

          {/* Video Controls */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 space-y-3">
            <div className="flex items-center gap-3">
              <button 
                onClick={togglePlay}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                data-testid="video-play-btn"
              >
                {isPlaying ? <Pause className="w-4 h-4 text-white" /> : <Play className="w-4 h-4 text-white" />}
              </button>
              <input
                type="range"
                min="0"
                max={videoDuration || 100}
                step="0.1"
                value={currentTime}
                onChange={handleSeek}
                onInput={handleSeek}
                className="flex-1 h-2 bg-zinc-700 rounded-full appearance-none cursor-pointer touch-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-grab [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0"
                style={{ padding: '6px 0' }}
                data-testid="video-seek-slider"
              />
              <span className="text-xs text-zinc-400 font-mono w-20 text-right">
                {formatTime(currentTime)} / {formatTime(videoDuration)}
              </span>
            </div>

            <Button
              onClick={captureFrame}
              className="w-full bg-white/10 hover:bg-white/20 text-white border border-zinc-700 rounded-lg text-sm"
              data-testid="capture-frame-btn"
            >
              <Camera className="w-4 h-4 mr-2" />
              Capture This Frame as Thumbnail
            </Button>
            {/* Mobile-friendly fallbacks — iOS Safari often blocks
                client-side canvas capture (taint / suspended decoder).
                These two paths always work on mobile. */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={useTimestampOnly}
                variant="outline"
                className="bg-zinc-800/60 hover:bg-zinc-800 text-zinc-200 border-zinc-700 rounded-lg text-xs"
                data-testid="use-timestamp-btn"
                title="Skip client-side capture — server extracts the frame at this time during upload"
              >
                Use frame at time
              </Button>
              <Button
                onClick={() => customThumbnailInputRef.current?.click()}
                variant="outline"
                className="bg-zinc-800/60 hover:bg-zinc-800 text-zinc-200 border-zinc-700 rounded-lg text-xs"
                data-testid="upload-custom-thumb-btn"
              >
                Upload custom image
              </Button>
            </div>
            <input
              ref={customThumbnailInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleCustomThumbnailPick}
              className="hidden"
              data-testid="custom-thumb-input"
            />
            <p className="text-[11px] text-zinc-500 text-center leading-tight">
              On mobile? Try &quot;Use frame at time&quot; if &quot;Capture&quot; fails, or upload your own image.
            </p>
          </div>
        </div>
      ) : (
        <div onClick={() => videoInputRef.current?.click()} className="aspect-video bg-zinc-900 border-2 border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors">
          <Video className="w-10 h-10 text-zinc-600 mb-3" />
          <p className="text-zinc-300 text-sm font-medium mb-2">Select Video File</p>
          <div className="text-center space-y-1">
            <p className="text-zinc-300 text-xs font-medium">720p · 60s minimum · complete song</p>
            <p className="text-zinc-500 text-[11px]">1280×720 horizontal · 720×1280 vertical</p>
            <p className="text-zinc-300 text-xs font-medium mt-2">MP4, MOV, WebM • Max 2GB</p>
            <p className="text-zinc-500 text-[11px]">Resumable direct upload — a dropped connection picks up where it stopped, not from zero</p>
          </div>
        </div>
      )}
      <input ref={videoInputRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mp4,.mov,.webm,.m4v" onChange={handleVideoSelect} className="hidden" data-testid="video-input" />
    </div>

    {/* Thumbnail Preview */}
    <div>
      <Label className="text-zinc-300 mb-2 block text-sm">Thumbnail Preview *</Label>
      {capturedThumbnail ? (
        <div className="relative aspect-video bg-zinc-900 rounded-lg overflow-hidden">
          <img src={capturedThumbnail} alt="Captured thumbnail" className="w-full h-full object-contain" />
          <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-white font-mono">
            {formatTime(thumbnailTimestamp)}
          </div>
          <button 
            onClick={() => setCapturedThumbnail(null)} 
            className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full hover:bg-black/80"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      ) : (
        <div className="aspect-video bg-zinc-900 border-2 border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center">
          <Image className="w-10 h-10 text-zinc-600 mb-2" />
          <p className="text-zinc-400 text-xs text-center px-4">
            {videoPreview 
              ? 'Scrub through your video and click "Capture This Frame"' 
              : 'Upload a video first'}
          </p>
        </div>
      )}
      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  </div>

  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-zinc-300 text-sm">Title *</Label>
        <SuggestAIButton axiosInstance={axiosInstance} contentType="music"
          getContext={() => [title, description, videoStyle, toolsUsed].filter(Boolean).join('. ')}
          onResult={(data) => {
            if (data.title) setTitle(data.title);
            if (data.description) setDescription((prev) => prev || data.description);
            setMusicSuggestedTags([...new Set(data.tags || [])]);
          }}
          disabled={uploading} />
      </div>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Video title"
        className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm focus:border-white" data-testid="video-title-input" />
    </div>
    <div className="space-y-1.5">
      <Label className="text-zinc-300 text-sm">Artist Name</Label>
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-md px-3 h-9 flex items-center">
        <span className="text-zinc-400 text-sm truncate" data-testid="video-artist-display">{artistName}</span>
      </div>
      <p className="text-zinc-500 text-xs">Your profile name is used for all videos</p>
    </div>
  </div>

  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
    <div className="space-y-1.5">
      <Label className="text-zinc-300 text-sm">Genre *</Label>
      <Select value={genre} onValueChange={setGenre}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm" data-testid="genre-select">
          <SelectValue placeholder="Select genre" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          {GENRES.map(g => (<SelectItem key={g} value={g} className="text-white hover:bg-zinc-800 text-sm">{g}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label className="text-zinc-300 text-sm">Style</Label>
      <Select value={videoStyle} onValueChange={setVideoStyle}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm" data-testid="style-select">
          <SelectValue placeholder="Select style (optional)" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          {VIDEO_STYLES.map(s => (<SelectItem key={s} value={s} className="text-white hover:bg-zinc-800 text-sm">{s}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-1.5">
      <Label className="text-zinc-300 text-sm">AI Tools Used</Label>
      <Input value={toolsUsed} onChange={(e) => setToolsUsed(e.target.value)} placeholder="Runway, Midjourney..."
        className="bg-zinc-900 border-zinc-800 text-white h-9 text-sm focus:border-white" data-testid="tools-input" />
    </div>
  </div>

  <div className="mb-4 space-y-1.5">
    <Label className="text-zinc-300 text-sm">Description</Label>
    <textarea value={description} onChange={(e) => setDescription(e.target.value)}
      placeholder="Describe your video..." rows={3}
      className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 py-2 text-sm focus:border-white focus:ring-1 focus:ring-white/20 outline-none resize-none"
      data-testid="video-description-input" />
    <SuggestedTags tags={musicSuggestedTags} onPick={(t) => setDescription((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}#${t}`)} />
  </div>

  {/* AI Disclosure — required (gates upload) */}
  <div className="mb-4">
    <AIDisclosureForm value={disclosure} onChange={setDisclosure} catalog={toolsCatalog || {}} />
  </div>

  <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800 cursor-pointer mb-3"
    onClick={() => setRightsConfirmed(!rightsConfirmed)} data-testid="rights-confirmation">
    <div className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all ${
      rightsConfirmed ? 'bg-white border-white' : 'border-zinc-600'}`}>
      {rightsConfirmed && (
        <svg className="w-2.5 h-2.5 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
    <p className="text-xs text-zinc-400 leading-relaxed">
      I confirm that I own all rights to this video and its content.
    </p>
  </div>

  {/* Content Certification */}
  <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800 cursor-pointer mb-4"
    onClick={() => setContentCertified(!contentCertified)} data-testid="content-certification">
    <div className={`mt-0.5 w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all ${
      contentCertified ? 'bg-orange-500 border-orange-500' : 'border-zinc-600'}`}>
      {contentCertified && (
        <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
    <div className="text-xs text-zinc-400 leading-relaxed">
      <p className="font-medium text-zinc-300 mb-1">I certify that this content does NOT contain:</p>
      <ul className="list-disc list-inside space-y-0.5 text-zinc-500">
        <li>Nudity, sexual, or explicit content</li>
        <li>Violence, gore, or graphic imagery</li>
        <li>Hate speech, discrimination, or harassment</li>
        <li>Illegal activities or drug use</li>
        <li>Self-harm or dangerous content</li>
        <li>Real artist names, celebrity likenesses, or label impersonation</li>
        <li>Lyric overlays, watermarks, or tool markers (e.g. "[verse 1]") in thumbnails</li>
      </ul>
      <p className="mt-2 text-zinc-500 italic">Violations will result in content removal and account suspension.</p>
    </div>
  </div>

  {uploading && (
    (() => {
      const stages = [
        { key: 'verify', label: 'Verifying resolution & duration', hint: 'Checking 720p + 60s requirements' },
        { key: 'audio', label: 'Analyzing audio with AI', hint: 'Whisper + GPT song verification' },
        { key: 'compress', label: 'Optimizing video for fast streaming', hint: 'This can take up to a minute for large files' },
        { key: 'finalize', label: 'Finalizing & saving to library', hint: 'Almost there…' },
      ];
      const activeIdx = processingStage ? stages.findIndex(s => s.key === processingStage) : -1;
      const elapsedLabel = processingElapsed > 0
        ? `${Math.floor(processingElapsed / 60)}:${String(processingElapsed % 60).padStart(2, '0')} elapsed`
        : '';
      return (
        <div className="mb-4 space-y-3" data-testid="upload-progress">
          {/* Byte-upload bar */}
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-zinc-400">
              <span data-testid="upload-progress-label">
                {progress < 100 ? 'Uploading to server…' : 'Upload complete — processing on server'}
              </span>
              <span data-testid="upload-progress-pct">{progress}%</span>
            </div>
            <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-white to-zinc-400"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Server-side processing stages — only appear after the byte upload finishes */}
          {progress >= 100 && (
            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 space-y-2.5"
              data-testid="processing-stages"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs text-zinc-300 font-medium flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-white" />
                  Server processing
                </p>
                {elapsedLabel && (
                  <span className="text-[11px] text-zinc-500 font-mono" data-testid="processing-elapsed">
                    {elapsedLabel}
                  </span>
                )}
              </div>
              <ul className="space-y-1.5">
                {stages.map((s, idx) => {
                  const isDone = activeIdx > idx;
                  const isActive = activeIdx === idx;
                  return (
                    <li
                      key={s.key}
                      className="flex items-start gap-2.5 text-xs"
                      data-testid={`processing-stage-${s.key}`}
                    >
                      <div
                        className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${
                          isDone
                            ? 'bg-emerald-500/20 border border-emerald-400/60'
                            : isActive
                            ? 'bg-white/10 border border-white/60'
                            : 'bg-zinc-800 border border-zinc-700'
                        }`}
                      >
                        {isDone ? (
                          <svg className="w-2.5 h-2.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : isActive ? (
                          <Loader2 className="w-2.5 h-2.5 text-white animate-spin" />
                        ) : null}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={isActive ? 'text-white font-medium' : isDone ? 'text-zinc-400 line-through' : 'text-zinc-500'}>
                          {s.label}
                        </p>
                        {isActive && s.hint && (
                          <p className="text-[10px] text-zinc-500 mt-0.5">{s.hint}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[10px] text-zinc-600 pt-1 border-t border-zinc-800">
                Your video is processing — this may take up to 10 minutes. Please keep this tab open while it finishes.
              </p>
            </div>
          )}
        </div>
      );
    })()
  )}

  <Button onClick={handleUpload} disabled={uploading || !rightsConfirmed || !contentCertified || !capturedThumbnail || (!customThumbnailFile && !useTimestampOnlySelected) || !isDisclosureComplete(disclosure)}
    className={`w-full font-semibold rounded-full text-sm ${
      rightsConfirmed && contentCertified && capturedThumbnail && isDisclosureComplete(disclosure) ? 'bg-white hover:bg-zinc-200 text-black' : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
    }`} data-testid="upload-submit-btn">
    {uploading ? (
      <>
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        {progress < 100
          ? `Uploading… ${progress}%`
          : processingStage === 'compress'
          ? 'Optimizing video…'
          : processingStage === 'audio'
          ? 'Analyzing audio…'
          : processingStage === 'finalize'
          ? 'Finalizing…'
          : 'Processing…'}
      </>
    ) : (
      <><Upload className="w-4 h-4 mr-2" />Upload Video</>
    )}
  </Button>
  </>
);
